/**
 * Renderers for the site-content micro-markup (see shared
 * `tenant-site-content/types.ts`): `**bold**`, `*italic*`, `- ` bullet rows,
 * and `\n` line breaks. Deliberately tiny — bespoke marketing pages need
 * exactly this much and nothing more (no HTML storage, no editor dependency).
 */
import { Fragment, type ReactNode } from 'react';

/** Inline `**bold**` / `*italic*` spans. */
export function renderInline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let key = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1] !== undefined) parts.push(<strong key={key++}>{m[1]}</strong>);
    else parts.push(<em key={key++}>{m[2]}</em>);
    last = m.index + m[0].length;
  }
  if (parts.length === 0) return text;
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Multi-line text field → lines separated by `<br/>` (hero titles). */
export function renderLines(text: string): ReactNode {
  const lines = text.split('\n');
  if (lines.length === 1) return renderInline(text);
  return lines.map((l, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {renderInline(l)}
    </Fragment>
  ));
}

/**
 * Paragraph rows → `<p>` elements; consecutive rows starting with `- ` group
 * into one `<ul>`. Use inside a container that styles bare p/ul/li.
 */
export function renderRichParas(paras: string[]): JSX.Element {
  const out: JSX.Element[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (bullets.length === 0) return;
    out.push(
      <ul key={`ul${out.length}`}>
        {bullets.map((b, i) => (
          <li key={i}>{renderInline(b)}</li>
        ))}
      </ul>,
    );
    bullets = [];
  };
  paras.forEach((p, i) => {
    if (p.startsWith('- ')) {
      bullets.push(p.slice(2));
      return;
    }
    flush();
    out.push(<p key={`p${i}`}>{renderInline(p)}</p>);
  });
  flush();
  return <>{out}</>;
}

/**
 * Paragraph rows joined into ONE flow with double line breaks between them —
 * for copy that must stay inside a single styled `<p>` (AGP's story body).
 */
export function renderParasJoined(paras: string[]): ReactNode {
  return paras.map((p, i) => (
    <Fragment key={i}>
      {i > 0 && (
        <>
          <br />
          <br />
        </>
      )}
      {renderInline(p)}
    </Fragment>
  ));
}
